import { useState, useRef, useEffect } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, XCircle, Eye, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

function fmtEUR(v) {
  if (v == null || v === '') return '-';
  return `\u20ac${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_BADGES = {
  draft_created: { label: 'Draft', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  none: { label: 'Not invoiced', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

export function InvoiceStatusPanel({ entityId, onViewInvoice }) {
  const [year, setYear] = useState(currentMonth > 1 ? currentYear : currentYear - 1);
  const [month, setMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const viewRef = useRef(null);

  // Scroll the inline view into sight when it opens
  useEffect(() => {
    if (viewingInvoice && viewRef.current) {
      viewRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [viewingInvoice, viewData]);

  const loadStatus = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = { entity_id: entityId, year, month };
      if (statusFilter) params.status_filter = statusFilter;
      const res = await invoicingAPI.status(params);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const viewInvoice = async (invoiceId) => {
    setViewingInvoice(invoiceId);
    setViewLoading(true);
    setViewData(null);
    try {
      const res = await invoicingAPI.viewInvoice(entityId, invoiceId);
      setViewData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setViewingInvoice(null);
    setViewData(null);
  };

  const retryBooking = async (uplistingId) => {
    try {
      await invoicingAPI.retryFailed(uplistingId);
      toast.success(`Cleared failed status for ${uplistingId}. Re-run Generate to retry.`);
      loadStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoice Status</CardTitle>
        <p className="text-sm text-muted-foreground">
          Bookings with their invoice status. Cancelled bookings with existing invoices are flagged.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All</option>
              <option value="draft_created">Draft created</option>
              <option value="failed">Failed</option>
              <option value="none">Not invoiced</option>
            </select>
          </div>
          <Button onClick={loadStatus} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Load
          </Button>
        </div>

        {result && (
          <div className="space-y-3">
            {/* Summary */}
            <div className="flex gap-4 text-sm">
              <span>Total: {result.total}</span>
              <span className="text-green-600">Drafted: {result.summary.draft_created}</span>
              <span className="text-red-600">Failed: {result.summary.failed}</span>
              <span className="text-muted-foreground">None: {result.summary.none}</span>
            </div>

            {/* Cancelled-with-invoice warning */}
            {result.cancelled_with_invoice_count > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  {result.cancelled_with_invoice_count} booking(s) cancelled AFTER being invoiced (credit note needed)
                </p>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-2 py-1.5 font-medium">ID</th>
                    <th className="text-left px-2 py-1.5 font-medium">Property</th>
                    <th className="text-left px-2 py-1.5 font-medium">Guest</th>
                    <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                    <th className="text-left px-2 py-1.5 font-medium">Channel</th>
                    <th className="text-right px-2 py-1.5 font-medium">Total</th>
                    <th className="text-center px-2 py-1.5 font-medium">Status</th>
                    <th className="text-center px-2 py-1.5 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((item) => (
                    <tr
                      key={item.uplisting_id}
                      className={`border-b ${item.cancelled_with_invoice ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
                    >
                      <td className="px-2 py-1 font-mono">{item.uplisting_id}</td>
                      <td className="px-2 py-1 max-w-[140px] truncate" title={item.property_name}>
                        {item.property_name}
                      </td>
                      <td className="px-2 py-1 max-w-[100px] truncate">{item.guest_name}</td>
                      <td className="px-2 py-1">{item.check_in}</td>
                      <td className="px-2 py-1">
                        <Badge variant="outline" className="text-[10px]">{item.channel}</Badge>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(item.total_price)}</td>
                      <td className="px-2 py-1 text-center">
                        {(() => {
                          const badge = STATUS_BADGES[item.invoice_status] || STATUS_BADGES.none;
                          return (
                            <Badge className={`text-[10px] ${badge.className}`}>
                              {badge.label}
                            </Badge>
                          );
                        })()}
                        {item.cancelled_with_invoice && (
                          <Badge className="text-[10px] ml-1 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                            Cancelled
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {item.pennylane_invoice_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => viewInvoice(item.pennylane_invoice_id)}
                            title="View invoice"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {item.invoice_status === 'failed' && (
                          <>
                            {item.pennylane_invoice_error && (
                              <span className="text-red-500 ml-1" title={item.pennylane_invoice_error}>
                                <XCircle className="h-3.5 w-3.5 inline" />
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 ml-1"
                              onClick={() => retryBooking(item.uplisting_id)}
                              title="Clear error and retry on next generate"
                            >
                              <RotateCcw className="h-3.5 w-3.5 text-blue-500" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Inline invoice view modal */}
        {viewingInvoice && (
          <div ref={viewRef} className="rounded-lg border p-4 bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">Live Invoice View (ID: {viewingInvoice})</p>
              <Button variant="ghost" size="sm" onClick={closeView}>Close</Button>
            </div>
            {viewLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : viewData ? (
              <InvoiceDetail data={viewData} />
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function computeLineTotal(line) {
  const qty = Number(line.quantity) || 0;
  const unitPrice = Number(line.raw_currency_unit_price ?? line.unit_price) || 0;
  const amount = Number(line.currency_amount ?? line.amount) || 0;
  if (unitPrice !== 0) return qty * unitPrice;
  return amount;
}

function InvoiceDetail({ data }) {
  const lines = data.invoice_lines || [];
  const lineSum = lines.reduce((acc, line) => acc + computeLineTotal(line), 0);
  const total = Number(data.amount) || 0;
  const diff = Math.abs(lineSum - total);
  const mismatch = lines.length > 0 && diff > 0.01;
  const hasCommission = lines.some(
    (l) => (l.label || '').toLowerCase().includes('commission') && Number(l.quantity) < 0
  );

  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Invoice #</p>
          <p className="font-medium">
            {data.draft
              ? <span className="text-muted-foreground italic">No legal invoice number until finalized</span>
              : (data.invoice_number || '-')}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Status</p>
          <p className="font-medium">{data.draft ? 'Draft' : (data.status || '-')}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Date</p>
          <p className="font-medium">{data.date || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Currency</p>
          <p className="font-medium">{data.currency || '-'}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Amount</p>
          <p className="font-medium">{data.amount != null ? fmtEUR(data.amount) : '-'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Customer</p>
          <p className="font-medium">
            {data.customer?.name
              || data.customer?.first_name
              || (typeof data.customer === 'string' ? data.customer : null)
              || '-'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Guest</p>
          <p className="font-medium">{data.guest_name || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Uplisting ID</p>
          <p className="font-medium font-mono">{data.uplisting_id || '-'}</p>
        </div>
      </div>

      {/* Lines */}
      {lines.length > 0 ? (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Invoice Lines</p>
          <div className="rounded border overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-2 py-1 font-medium">Label</th>
                  <th className="text-right px-2 py-1 font-medium">Qty</th>
                  <th className="text-right px-2 py-1 font-medium">Unit Price</th>
                  <th className="text-right px-2 py-1 font-medium">Amount</th>
                  <th className="text-right px-2 py-1 font-medium">VAT</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-2 py-1">{line.label || line.description || '-'}</td>
                    <td className="px-2 py-1 text-right">{line.quantity}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {(line.raw_currency_unit_price ?? line.unit_price) != null ? fmtEUR(line.raw_currency_unit_price ?? line.unit_price) : '-'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(computeLineTotal(line))}</td>
                    <td className="px-2 py-1 text-right">{line.vat_rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Reconciliation */}
          <div className="space-y-1 mt-1">
            <div className="flex justify-between text-xs font-medium pt-1 border-t">
              <span>Sum of lines</span>
              <span className="tabular-nums">{fmtEUR(lineSum)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span>Pennylane total</span>
              <span className="tabular-nums">{fmtEUR(total)}</span>
            </div>
            {mismatch && (
              <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                Line sum ({fmtEUR(lineSum)}) does not match Pennylane total ({fmtEUR(total)}).
                Difference: {fmtEUR(diff)}.
              </div>
            )}
            {!hasCommission && total > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                No commission deduction line. Total may be gross (not net revenue).
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 p-3 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">No line items returned by Pennylane for this {data.draft ? 'draft' : 'invoice'}.</span>
        </div>
      )}

      {/* Links */}
      <div className="flex gap-3">
        {data.pennylane_url && (
          <a
            href={data.pennylane_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 underline"
          >
            Open in Pennylane
          </a>
        )}
        {data.pdf_url && (
          <a
            href={data.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 underline"
          >
            Download PDF
          </a>
        )}
      </div>
    </div>
  );
}
